import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";

// CRM dashboard queue: jobs flagged "Protection Plan opted" that reached Delivery+ and don't yet
// have an active plan. Mirrors the warranty pending list.
export const ProtectionTasks = () => {
  const [tasks, setTasks] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/protection-plans/opted-pending")
      .then(({ data }) => setTasks(data.tasks || []))
      .catch(() => setTasks([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border-brand-line shadow-none" data-testid="protection-tasks-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-brand" /> Protection Plan pending
          </CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Jobs marked “Protection Plan opted” that reached Delivery — run the opt-in &amp; registration.
          </p>
        </div>
        <Badge className="border-0 bg-brand-tint text-brand" data-testid="protection-tasks-count">
          {tasks.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        {tasks.length === 0 && (
          <p className="rounded-lg border border-dashed border-brand-line p-4 text-center text-sm text-slate-400"
             data-testid="protection-tasks-empty">
            No protection plans pending.
          </p>
        )}
        {tasks.map((t) => (
          <div
            key={t.job_id}
            data-testid={`protection-task-${t.job_id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand-line bg-white p-3"
          >
            <div>
              <p className="text-sm font-medium text-brand-ink">
                {t.customer_name || "Customer"}
                {t.vehicle && <span className="text-slate-500"> · {t.vehicle}</span>}
              </p>
              <p className="text-xs text-slate-500">
                {t.studio_name} · {t.status}
                {t.delivered_on && ` · delivered ${fmtDate(t.delivered_on)}`}
                {t.days_pending > 0 && ` · ${t.days_pending} day(s) pending`}
                {t.plan_status && ` · plan: ${t.plan_status.replace(/_/g, " ")}`}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="min-h-[40px]"
              data-testid={`protection-task-open-${t.job_id}`}
              onClick={() => navigate(`/jobs/${t.job_id}`)}
            >
              Open job
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default ProtectionTasks;
